import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DirectusService } from '../core/services/directus.service';
import { WorkbenchField } from './workbench.model';

@Component({
  selector: 'app-entity-workbench',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './entity-workbench.component.html',
  styleUrl: './entity-workbench.component.scss'
})
export class EntityWorkbenchComponent implements OnInit {
  @Input({ required: true }) title = '';
  @Input() description = '';
  @Input({ required: true }) collection = '';
  @Input({ required: true }) fields: WorkbenchField[] = [];
  @Input() sort = '-id';
  @Input() query: Record<string, string | number | boolean | undefined> = {};

  items: Record<string, unknown>[] = [];
  selectedId = '';
  form: Record<string, unknown> = {};
  createMode = false;
  loading = false;
  saving = false;
  deleting = false;
  confirmDialogOpen = false;
  message = '';
  error = '';

  constructor(private readonly directus: DirectusService) {}

  async ngOnInit(): Promise<void> {
    await this.loadItems();
  }

  itemLabel(item: Record<string, unknown>): string {
    const keys = ['title', 'name', 'slug', 'email'];
    for (const key of keys) {
      const value = item[key];
      if (typeof value === 'string' && value.trim()) return value;
    }

    return `Item ${String(item['id'] || '')}`.trim();
  }

  private normalizeFieldValue(type: WorkbenchField['type'], value: unknown): unknown {
    if (type === 'number') return value === '' ? null : Number(value);
    if (type === 'boolean') return Boolean(value);
    if (type === 'json') {
      if (typeof value === 'string' && value.trim()) return JSON.parse(value);
      return null;
    }
    return value;
  }

  private formFromItem(item: Record<string, unknown> | null): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const field of this.fields) {
      const value = item?.[field.key];
      if (field.type === 'json') {
        out[field.key] = value ? JSON.stringify(value, null, 2) : '';
      } else if (field.type === 'boolean') {
        out[field.key] = Boolean(value);
      } else if (field.type === 'datetime') {
        // datetime-local inputs require "YYYY-MM-DDTHH:MM" — strip seconds/timezone
        out[field.key] = value ? String(value).substring(0, 16) : '';
      } else if (field.type === 'date') {
        // date inputs require "YYYY-MM-DD"
        out[field.key] = value ? String(value).substring(0, 10) : '';
      } else {
        out[field.key] = value ?? '';
      }
    }
    return out;
  }

  async loadItems(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      this.items = await this.directus.list(this.collection, {
        limit: 100,
        sort: this.sort,
        fields: '*.*',
        ...this.query
      });
    } catch (error: unknown) {
      this.error = error instanceof Error ? error.message : 'Failed to load items';
    } finally {
      this.loading = false;
    }
  }

  selectItem(item: Record<string, unknown>): void {
    this.createMode = false;
    this.selectedId = String(item['id'] || '');
    this.form = this.formFromItem(item);
    this.message = '';
    this.error = '';
  }

  isSelected(item: Record<string, unknown>): boolean {
    return this.selectedId === String(item['id'] || '') && !this.createMode;
  }

  startCreate(): void {
    this.createMode = true;
    this.selectedId = '';
    this.form = this.formFromItem(null);
    this.message = '';
    this.error = '';
  }

  closeDrawer(): void {
    this.selectedId = '';
    this.createMode = false;
    this.message = '';
    this.error = '';
  }

  private payload(): Record<string, unknown> {
    const payload: Record<string, unknown> = {};
    for (const field of this.fields) {
      payload[field.key] = this.normalizeFieldValue(field.type, this.form[field.key]);
    }
    return payload;
  }

  async save(): Promise<void> {
    this.saving = true;
    this.message = '';
    this.error = '';
    try {
      const payload = this.payload();

      if (this.createMode) {
        await this.directus.create(this.collection, payload);
        this.message = 'Created successfully.';
        this.closeDrawer();
      } else if (this.selectedId) {
        await this.directus.update(this.collection, this.selectedId, payload);
        this.message = 'Saved successfully.';
      } else {
        this.error = 'Select an item or create a new one.';
      }

      await this.loadItems();
    } catch (error: unknown) {
      this.error = error instanceof Error ? error.message : 'Save failed';
    } finally {
      this.saving = false;
    }
  }

  async deleteSelected(): Promise<void> {
    if (!this.selectedId || this.createMode) return;
    this.confirmDialogOpen = true;
  }

  closeConfirmDialog(): void {
    if (this.deleting) return;
    this.confirmDialogOpen = false;
  }

  async confirmDeleteSelected(): Promise<void> {
    if (!this.selectedId || this.createMode) return;

    this.deleting = true;
    this.message = '';
    this.error = '';
    try {
      await this.directus.remove(this.collection, this.selectedId);
      this.message = 'Deleted successfully.';
      this.selectedId = '';
      this.form = this.formFromItem(null);
      await this.loadItems();
    } catch (error: unknown) {
      this.error = error instanceof Error ? error.message : 'Delete failed';
    } finally {
      this.deleting = false;
      this.confirmDialogOpen = false;
    }
  }
}
